import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import debounce from "lodash/debounce";
import { createSelector } from "reselect";
import classNames from "classnames";
import { Checkbox, CheckboxProps } from "common/DS/Checkables";
import { formatPhone } from "lib/util";
import { usePost } from "redux/fetch-hooks";
import { useSelector, useDispatch } from "react-redux";
import { checkinSetNudged } from "redux/checkin/actions";
import TextBubble from "common/DS/TextBubble";
import { CHECKIN_TYPE, QUESTION_TYPE } from "lib/constants";
import { TranslationFn } from "lib/types";

import QuestionButtonGroup from "./QuestionButtonGroup";
import Textarea from "./QuestionTextarea";
import { FollowUpProps, StateType } from "./types";

// after this many characters, consider a previous prefetch to be invalid
const PREFETCH_CHAR_COUNTDOWN = 20;
const FOLLOWUP_MAX_CHARS = 200;
const MAX_LENGTH = 5000;

type NudgeDataProps = {
    extraData: FollowUpProps["extraData"];
    id: FollowUpProps["id"];
    questionType: number | undefined;
    cleanComment: string;
    selectedAnswerData: FollowUpProps["selectedAnswerData"];
};

const LengthCounter = ({ length, maxLength }: { length: number; maxLength: number }) => {
    const remainingChars = maxLength - length;
    return (
        <span className="flex flex-jsb">
            {remainingChars < 50 && (
                <span className="text-nowrap">
                    {length}
                    {maxLength > 0 && `/${maxLength}`}
                </span>
            )}
        </span>
    );
};

const getNudgeData = ({
    extraData,
    id,
    questionType,
    cleanComment,
    selectedAnswerData,
}: NudgeDataProps) => ({
    ...extraData,
    question_id: id,
    comment: cleanComment,
    ...(questionType === QUESTION_TYPE.LONG_ANSWER
        ? {}
        : {
              value: selectedAnswerData,
              response: selectedAnswerData?.responseText || selectedAnswerData?.response,
          }),
});

const GuidedTextarea = (
    {
        questionText,
        id,
        handleBack,
        handleChange,
        handleSubmit: handleQuestionSubmit,
        handleSkip,
        isLastQuestion,
        extraData,
        selfIdentifyOff,
        selectedAnswerData,
        isAnonymous,
        toggleAnonymous,
        additionalClassName,
        isRequired,
        visible = true,
        placeholder,
        dataTest,
        checkinRemoveConfidentiality,
        checkinNonConfidential,
        imageUrl,
    }: Omit<FollowUpProps, "optional" | "handleSelfIdentify">,
    { t: _ }: { t: TranslationFn },
) => {
    const [hasError, setHasError] = useState(false);
    const [wasVisible, setWasVisible] = useState(false);
    const [selfIdentifyDisabled, setSelfIdentifyDisabled] = useState(true);
    const [comment, setComment] = useState("");
    const [cleanComment, setCleanComment] = useState("");
    const [nudgeUpValue, setNudgeUpValue] = useState("");
    const [nudge, setNudge] = useState<string | null>(null);
    const [prefetchedNudge, setPrefetchedNudge] = useState<string | null>(null);
    const [prefetchValidChars, setPrefetchValidChars] = useState(0);
    const [prefetchLoading, setPrefetchLoading] = useState(false);
    const [showPrefetch, setShowPrefetch] = useState(false);
    const [showBubble, setShowBubble] = useState(false);
    const [fetchNudge, { isLoading }] = usePost<{ nudge?: string }>("/api/v1/followup-nudge", {
        logOnCriticalOnly: true,
    });
    const { user, hire_id: hireId } = extraData || {};

    const signature = useMemo(() => {
        if (!user) {
            return "";
        }
        return `\n-\n${user.full_name || ""}\n${user.email || ""}\n${formatPhone(
            user.phone || "",
        )}`;
    }, [extraData?.user]);

    const maxLength = MAX_LENGTH - signature.length - 1;
    // Overall submitted length should be less than MAX_LENGTH (5000).
    // The final submitted value at most is comment + \n\n + nudgeUpValue + signature.
    // nudgeUpValue will only have anything in it if comment is less than FOLLOWUP_MAX_CHARS (200),
    // which is way less than MAX_LENGTH so we won't get any crazy short nudgeUpMaxLength.
    const nudgeUpMaxLength = maxLength - comment.length - 2;

    const formatNudgeSubmitValue = (val: string) => {
        const formatted = `${val.replace(signature, "")}\n\n${nudgeUpValue}`;
        if (!isAnonymous) {
            return `${formatted}${signature}`;
        }
        return formatted;
    };
    const formatFollowUpValue = (val: string) => {
        if (!isAnonymous) {
            return `${val}${signature}`;
        }
        return val;
    };
    const setIsAnonymous = (val: boolean) => {
        toggleAnonymous(val);
    };
    const submitValue = nudge ? formatNudgeSubmitValue(comment) : formatFollowUpValue(comment);
    const nudged = useSelector<StateType, boolean>((state) => state.nudged);
    const submitting = useSelector<StateType, boolean>((state) => state.ws.ui.submitting);
    const handleSubmit = (val: string) => {
        if (handleQuestionSubmit && !submitting) {
            handleQuestionSubmit(val);
        }
    };
    const dispatch = useDispatch();
    const settings = useSelector<StateType, StateType["settings"]>((state) => state.settings);
    const questionType = useSelector<StateType, number | undefined>(
        createSelector(
            (state: StateType) => state.questions.current,
            (state: StateType) => state.questions.questions,
            (current, questions) => questions?.[current]?.type,
        ),
    );

    const finalizeNudge = (nudgeText: string) => {
        fetchNudge({
            data: {
                ...getNudgeData({
                    extraData,
                    id,
                    questionType,
                    cleanComment,
                    selectedAnswerData,
                }),
                nudge: nudgeText,
                finalize: 1,
            },
        });
    };
    const doNudge = () => {
        setShowBubble(true);
        if (prefetchLoading) {
            setShowPrefetch(true);
            return;
        }
        if (prefetchedNudge && prefetchValidChars > 0) {
            finalizeNudge(prefetchedNudge);
            // fake a little delay to show the nudge
            setTimeout(() => setNudge(prefetchedNudge), 500);

            dispatch(checkinSetNudged(true));
            return;
        }
        if (comment) {
            fetchNudge({
                data: getNudgeData({
                    extraData,
                    id,
                    questionType,
                    cleanComment,
                    selectedAnswerData,
                }),
                onSuccess: (data) => {
                    if (data.nudge) {
                        setNudge(data.nudge);
                        dispatch(checkinSetNudged(true));
                    }
                },
                onError: () => {
                    // fail invisibly, just go to the next question
                    handleSubmit(submitValue);
                },
            });
        }
    };

    const followUpRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (showPrefetch && prefetchedNudge) {
            // user clicked next, we have a nudge to show
            finalizeNudge(prefetchedNudge);
            setTimeout(() => setNudge(prefetchedNudge), 500);

            dispatch(checkinSetNudged(true));
        }
        if (showPrefetch && !prefetchLoading && !prefetchedNudge) {
            // user clicked next, we don't have a nudge to show
            handleSubmit(submitValue);
        }
    }, [showPrefetch, prefetchedNudge, prefetchLoading]);

    const prefetchNudge = useCallback(
        debounce(
            (answerData: NudgeDataProps) => {
                setPrefetchLoading(true);
                fetchNudge({
                    data: {
                        ...getNudgeData(answerData),
                        prefetch: 1,
                    },
                    onSuccess: (data) => {
                        if (data.nudge) {
                            setPrefetchedNudge(data.nudge);
                        }
                        setPrefetchLoading(false);
                    },
                    onError: () => {
                        setPrefetchedNudge(null);
                        setPrefetchLoading(false);
                    },
                });
            },
            500,
            { trailing: true },
        ),
        [fetchNudge, setPrefetchedNudge, setPrefetchLoading],
    );
    const language = useSelector<StateType, StateType["ws"]["language"]>(
        (state) => state.ws.language,
    );

    const brightPromptEnabled = useSelector<StateType>(
        (state) =>
            !state.company?.settings?.disable_bright_prompt &&
            (state.ws?.language == null || state.ws?.language?.startsWith("en")) &&
            state.checkin_type !== CHECKIN_TYPE.TERMINATION &&
            !(state.settings?.GUIDED_FOLLOWUP_MODE === "none"),
    );

    const nudgeOnce = settings?.GUIDED_FOLLOWUP_MODE === "once";

    const shouldNudge =
        !(nudgeOnce && nudged) &&
        brightPromptEnabled &&
        comment.length < FOLLOWUP_MAX_CHARS &&
        comment.length > 3 &&
        !nudge &&
        language &&
        language.startsWith("en");
    const handleInputChange = (value: string) => {
        const isError = !value && isRequired;
        const val = isError ? "" : value;
        setHasError(isError);
        setComment(val);
        setCleanComment(val);
        setSelfIdentifyDisabled(val === "");
        setPrefetchValidChars((prev) => (prev || 0) - 1);

        if (
            shouldNudge &&
            (!prefetchedNudge || prefetchValidChars <= 0) &&
            val &&
            !prefetchLoading
        ) {
            setPrefetchValidChars(PREFETCH_CHAR_COUNTDOWN);
            prefetchNudge({
                extraData,
                id,
                cleanComment,
                selectedAnswerData,
                questionType,
            });
        }

        if (handleChange) {
            handleChange(val);
        }
    };

    const onChange: React.ChangeEventHandler<HTMLInputElement> = ({ target }) =>
        handleInputChange(target.value);

    const onSubmit = () => {
        if (shouldNudge) {
            doNudge();
        } else if (handleSubmit) {
            handleSubmit(submitValue);
        }
    };

    const onSkip: React.MouseEventHandler<HTMLButtonElement> = (e) => {
        if (showBubble) {
            // the user has already been nudged, this "skip" is just a "next"
            return handleSubmit(submitValue);
        }
        return handleSkip(e);
    };

    const onSelfIdentify: CheckboxProps["onChange"] = ({ target }, checked) => {
        target.blur();
        setIsAnonymous(!checked);
    };

    useEffect(() => {
        if (visible) {
            followUpRef.current?.focus();
            if (wasVisible) {
                handleInputChange(comment);
            } else {
                setWasVisible(true);
            }
        }
    }, [visible]);

    useEffect(() => {
        if (hasError && followUpRef.current) {
            followUpRef.current?.focus();
        }
    }, [hasError]);

    const renderError = () => {
        if (!hasError) {
            return null;
        }

        return <span className="help-block">This field is required.</span>;
    };

    const renderSelfIdentify = () => {
        if (
            selfIdentifyOff ||
            !user ||
            !(user.full_name || user.email || user.phone) ||
            !hireId ||
            checkinRemoveConfidentiality ||
            checkinNonConfidential
        ) {
            return null;
        }

        return (
            <Checkbox
                checked={!isAnonymous}
                onChange={onSelfIdentify}
                disabled={selfIdentifyDisabled}
                data-test="self-identify-checkbox"
            >
                <span
                    className="questions__follow-up-self-identify"
                    /* eslint-disable react/no-danger */
                    dangerouslySetInnerHTML={{
                        __html: _(
                            "Share my identity <strong>for this question only</strong> so I can be contacted",
                        ),
                    }}
                    /* eslint-enable react/no-danger */
                />
            </Checkbox>
        );
    };

    const style: { display?: string } = {};
    if (!visible) {
        style.display = "none";
    }

    const nextDisabled = !comment.length || (isLoading && !prefetchLoading);

    const wrapperClass = classNames("questions__follow-up label-group", additionalClassName);

    return (
        <div className={wrapperClass} style={style}>
            <label htmlFor={`follow_up-${id}`}>{questionText}</label>
            {imageUrl && (
                <div className="questions__image-container">
                    <img src={imageUrl} alt="question related" />
                </div>
            )}
            {showBubble && (
                <div className="questions__follow-up__initial_answer">
                    <TextBubble loaded={!!nudge} text={cleanComment} />
                </div>
            )}
            {!showBubble && (
                <Textarea
                    disabled={(isLoading && !prefetchLoading) || !!nudge}
                    id={`follow_up-${id}`}
                    name="follow_up"
                    data-test={dataTest || "follow-up-answer-text-area"}
                    placeholder={placeholder || _("Let us know how we can improve")}
                    onChange={onChange}
                    inputProps={{ maxLength }}
                    inputRef={followUpRef}
                    helperText={<LengthCounter length={comment.length} maxLength={maxLength} />}
                />
            )}
            {nudge && (
                <>
                    <Box className="questions__follow-up-nudge">{nudge}</Box>
                    <Textarea
                        id={`nudge_up-${id}`}
                        name="nudge_up"
                        data-test="follow-up-nudge-text-area"
                        placeholder=""
                        onChange={(e) => setNudgeUpValue(e.target.value)}
                        inputProps={{ maxLength: nudgeUpMaxLength }}
                        inputRef={followUpRef}
                        helperText={
                            <LengthCounter
                                length={nudgeUpValue.length}
                                maxLength={nudgeUpMaxLength}
                            />
                        }
                    />
                </>
            )}
            {renderSelfIdentify()}
            {renderError()}
            <QuestionButtonGroup
                nextDisabled={submitting || nextDisabled}
                handleSkip={onSkip}
                handleSubmit={onSubmit}
                handleBack={handleBack}
                isLastQuestion={isLastQuestion}
                forceShowSkip={!isRequired}
                isRequired={isRequired}
                loading={submitting || (isLoading && !prefetchLoading)}
            />
        </div>
    );
};

GuidedTextarea.contextTypes = {
    t: PropTypes.func,
};

export default GuidedTextarea;
